import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/shared/ui/button';

// Le tour guidé du premier passage : des bulles qui montrent l'application, une fois.
//
// SANS AUCUNE BIBLIOTHÈQUE, et ce n'est pas une coquetterie. Les tours produits du
// marché (Joyride, Shepherd, Intro.js) pèsent entre 30 et 90 Ko pour ce que fait ce
// fichier, et imposent leur propre gestion du focus, leur propre thème et leur propre
// idée de ce qu'est une étape. ATL-6 a livré un glisser-déposer sans en ajouter une :
// c'est la même règle ici.
//
// CE QU'IL NE FAIT JAMAIS
//
// 1. **Bloquer.** Aucune étape n'est obligatoire, la croix ferme, `Échap` ferme, un clic
//    hors de la bulle ferme. Un tour qu'on ne peut pas fuir est une porte, pas une aide.
// 2. **Pointer un élément absent.** Chaque rôle voit des écrans différents, et une même
//    page n'affiche pas tout à tout le monde. Une étape dont la cible n'existe pas est
//    SAUTÉE, jamais affichée dans le vide.
//
//    ⚠️ Ce n'est pas une précaution théorique : la PREMIÈRE version de ce fichier visait
//    six identifiants que j'avais SUPPOSÉS (`nav-dashboard`, `nav-clients`,
//    `new-client-btn`…). Aucun n'existait — la barre latérale n'en pose pas, et le bouton
//    de création s'appelle `add-client-btn`. Le tour se serait affiché vide pour l'admin
//    et le commercial, sans qu'aucune erreur ne remonte : une étape sautée ne se plaint
//    pas. C'est `test_tour_guide.py` qui l'a attrapé, et c'est sa raison d'être.
// 3. **Se rejouer tout seul.** Une fois terminé ou fermé, il ne revient pas — sauf
//    demande explicite depuis l'aide.
//
// ⚠️ OÙ VIT LE SOUVENIR, ET CE QUE ÇA COÛTE
//
// Dans `localStorage`, sous une clé qui porte l'identifiant de l'utilisateur. Ce n'est
// pas le meilleur endroit : changer de navigateur rejoue le tour, et le même compte
// ouvert sur un téléphone et sur un poste le verra deux fois.
//
// Le bon endroit serait le document `user`, côté serveur. Ce n'est pas fait, et la
// raison est un arbitrage, pas un oubli : il n'existe aujourd'hui AUCUN mécanisme de
// préférences utilisateur dans ce dépôt. En créer un pour un tour produit demanderait
// une route neuve, sa garde, sa place dans la matrice des droits et sa migration — pour
// mémoriser qu'on a vu six bulles. La dette est écrite ici plutôt que tue : le jour où
// une deuxième préférence apparaîtra, les deux monteront ensemble côté serveur.

const CLE = (utilisateur) => `flexcore_tour_vu_${utilisateur || 'anonyme'}`;

// Les étapes, par rôle. Chacune vise un `data-testid` DÉJÀ POSÉ par l'écran — on n'en
// ajoute pas pour le tour, sinon le tour devient une raison de ne pas les renommer.
//
// Trois à cinq étapes par rôle, pas davantage. Un tour de quinze bulles ne se lit pas :
// il se clique jusqu'à la fin sans être lu, ce qui est pire que pas de tour du tout.
//
// Le rôle `distributeur` n'a pas de tour : son seul écran est le SAV, il n'a pas
// d'accueil propre, et une bulle unique n'est pas un tour — c'est une interruption.
export const ETAPES_PAR_ROLE = {
  admin: [
    { cible: '[data-testid="sidebar"]', titre: 'Votre navigation',
      texte: "Tous les écrans de votre espace. Le champ de recherche en haut y trouve un écran par son nom." },
    { cible: '[data-testid="clients-page"]', titre: 'Vos clients',
      texte: "La liste, ses filtres, et l'export. Le compteur dit combien de fiches répondent aux filtres actifs." },
    { cible: '[data-testid="add-client-btn"]', titre: 'Créer un client',
      texte: "L'adresse se propose pendant que vous tapez : choisissez-la et le code postal, la ville et la région se remplissent. L'adresse email est obligatoire — c'est elle qui porte le mandat." },
    { cible: '[data-testid="export-clients-csv-btn"]', titre: 'Exporter',
      texte: "La liste filtrée, au format tableur. Ce que vous voyez est ce qui sort." },
    { cible: '[data-testid="alert-badge-count"]', titre: 'Les alertes',
      texte: "Ce qui demande votre attention : un envoi qui n'est pas arrivé, un équipement muet, une intégration tombée." },
  ],
  commercial: [
    { cible: '[data-testid="sidebar"]', titre: 'Votre navigation',
      texte: "Vos écrans. Le champ de recherche en haut y trouve un écran par son nom." },
    { cible: '[data-testid="add-client-btn"]', titre: 'Créer un client',
      texte: "L'adresse se propose pendant la frappe. L'adresse email est obligatoire : c'est à elle que part le mandat signé." },
    { cible: '[data-testid="clients-results-count"]', titre: 'Ce que vous voyez',
      texte: "Le compteur suit les filtres. Une liste tronquée le dit, elle ne fait pas semblant d'être complète." },
  ],
  installateur: [
    { cible: '[data-testid="installateur-home"]', titre: 'Vos chantiers',
      texte: "Ceux de vos poseurs y figurent aussi, avec leurs attestations de fin de travaux." },
    { cible: '[data-testid="inst-see-team-btn"]', titre: 'Votre équipe',
      texte: "Vos poseurs, et ce que chacun a posé." },
    { cible: '[data-testid="inst-quick-create-poseur"]', titre: 'Ajouter un poseur',
      texte: "Il recevra ses accès par courriel et pourra ouvrir des chantiers sous votre nom." },
    // ⚠️ UNE ÉTAPE « CE QUI ATTEND DU MATÉRIEL » A ÉTÉ RETIRÉE ICI LE 10/09/2026, POUR
    //    L'INSTALLATEUR COMME POUR LE POSEUR. Elle pointait le bandeau de l'accueil,
    //    supprimé ce jour-là : « ils ne doivent pas voir en attente de matériel. Toutes
    //    ces infos remontent dans les alertes pour l'admin opérateur de flexcore. »
    //    Une étape qui vise une ancre absente est sautée EN SILENCE — le tour rétrécit
    //    sans que personne ne s'en aperçoive, et c'est exactement ce que
    //    `test_tour_guide.py` interdit.
  ],
  poseur: [
    { cible: '[data-testid="poseur-home"]', titre: 'Vos chantiers',
      texte: "Ce que vous avez posé, et ce qui reste à finir." },
    { cible: '[data-testid="poseur-new-chantier-btn"]', titre: 'Commencer un chantier',
      texte: "Le parcours guidé : le client, son équipement, le mandat, la fin de chantier. L'adresse se propose pendant la frappe." },
    // ⚠️ UNE ÉTAPE « CE QUI ATTEND DU MATÉRIEL » A ÉTÉ RETIRÉE ICI LE 10/09/2026, POUR
    //    L'INSTALLATEUR COMME POUR LE POSEUR. Elle pointait le bandeau de l'accueil,
    //    supprimé ce jour-là : « ils ne doivent pas voir en attente de matériel. Toutes
    //    ces infos remontent dans les alertes pour l'admin opérateur de flexcore. »
    //    Une étape qui vise une ancre absente est sautée EN SILENCE — le tour rétrécit
    //    sans que personne ne s'en aperçoive, et c'est exactement ce que
    //    `test_tour_guide.py` interdit.
    { cible: '[data-testid="poseur-see-all-chantiers"]', titre: 'Tout revoir',
      texte: "L'historique complet, au-delà des derniers dossiers." },
  ],
};

const MARGE = 12;

export default function TourGuide({ utilisateur, role, forcer = false, onFin }) {
  const etapesDuRole = useMemo(() => ETAPES_PAR_ROLE[role] || [], [role]);
  const [ouvert, setOuvert] = useState(false);
  const [index, setIndex] = useState(0);
  const [boite, setBoite] = useState(null);
  const bulle = useRef(null);

  // Les étapes dont la cible EXISTE vraiment à l'écran. Calculé à l'ouverture, une fois :
  // un rôle qui n'a pas l'écran d'une étape ne doit pas voir une bulle dans le vide.
  const [etapes, setEtapes] = useState([]);

  useEffect(() => {
    if (!role) return undefined;
    const dejaVu = (() => {
      try { return localStorage.getItem(CLE(utilisateur)) === '1'; } catch { return true; }
    })();
    if (dejaVu && !forcer) return undefined;

    // On laisse l'écran finir de se monter : un tour qui mesure des positions avant que
    // la page ait fini de charger les place toutes en haut à gauche.
    const minuteur = setTimeout(() => {
      const presentes = etapesDuRole.filter(e => document.querySelector(e.cible));
      if (presentes.length === 0) return;
      setEtapes(presentes);
      setIndex(0);
      setOuvert(true);
    }, 900);
    return () => clearTimeout(minuteur);
  }, [role, utilisateur, forcer, etapesDuRole]);

  // La position de la bulle suit sa cible, y compris au redimensionnement et au
  // défilement — sans quoi elle reste accrochée à un endroit que l'élément a quitté.
  useEffect(() => {
    if (!ouvert || !etapes[index]) return undefined;
    const placer = () => {
      const cible = document.querySelector(etapes[index].cible);
      if (!cible) { suivant(); return; }
      cible.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setBoite(cible.getBoundingClientRect());
    };
    placer();
    window.addEventListener('resize', placer);
    window.addEventListener('scroll', placer, true);
    return () => {
      window.removeEventListener('resize', placer);
      window.removeEventListener('scroll', placer, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert, index, etapes]);

  useEffect(() => {
    if (!ouvert) return undefined;
    const touche = (e) => { if (e.key === 'Escape') fermer(); };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert]);

  function fermer() {
    setOuvert(false);
    try { localStorage.setItem(CLE(utilisateur), '1'); } catch { /* navigation privée */ }
    if (onFin) onFin();
  }

  function suivant() {
    if (index + 1 >= etapes.length) { fermer(); return; }
    setIndex(i => i + 1);
  }

  if (!ouvert || !etapes[index] || !boite) return null;

  const etape = etapes[index];
  const enBas = boite.bottom + 190 < window.innerHeight;
  const style = {
    position: 'fixed',
    top: enBas ? boite.bottom + MARGE : Math.max(MARGE, boite.top - 190),
    left: Math.min(Math.max(MARGE, boite.left), window.innerWidth - 340),
    width: 320,
    zIndex: 60,
  };

  return (
    <>
      {/* Le voile assombrit SANS masquer la cible : c'est un trou, pas un rideau.
          Un tour qui cache ce qu'il désigne oblige à le fermer pour comprendre. */}
      <div
        className="fixed inset-0 z-50 bg-black/40"
        style={{
          clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0,
            ${boite.left - 4}px ${boite.top - 4}px,
            ${boite.left - 4}px ${boite.bottom + 4}px,
            ${boite.right + 4}px ${boite.bottom + 4}px,
            ${boite.right + 4}px ${boite.top - 4}px,
            ${boite.left - 4}px ${boite.top - 4}px)`,
        }}
        onClick={fermer}
        data-testid="tour-voile"
      />
      <div ref={bulle} style={style}
        className="rounded-lg border border-neutral-200 bg-white p-4 shadow-xl"
        data-testid="tour-bulle">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[13px] font-semibold text-neutral-900">{etape.titre}</p>
          <button type="button" onClick={fermer} aria-label="Fermer le guide"
            className="text-neutral-400 hover:text-neutral-700 text-lg leading-none"
            data-testid="tour-fermer">×</button>
        </div>
        <p className="mt-1.5 text-[12px] leading-relaxed text-neutral-600">{etape.texte}</p>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-neutral-400" data-testid="tour-progression">
            {index + 1} / {etapes.length}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={fermer} data-testid="tour-passer">
              Passer
            </Button>
            <Button size="sm" onClick={suivant} data-testid="tour-suivant">
              {index + 1 >= etapes.length ? 'Terminer' : 'Suivant'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

/** Rejouer le tour, depuis l'aide. Sans cela, un utilisateur qui a cliqué « Passer » le
 *  premier jour n'a plus aucun moyen de le revoir. */
export function reinitialiserTour(utilisateur) {
  try { localStorage.removeItem(CLE(utilisateur)); } catch { /* navigation privée */ }
}
